import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import { customerKeys, toCustomer, useCreateCustomer } from "@/features/customer/api";
import type {
  Lead,
  LeadCreateRequest,
  LeadLostReason,
  LeadPatchRequest,
  LeadWire,
} from "@/features/lead/types";
import type { CustomerCreateRequest, CustomerWire } from "@/types/customer";

// ---------------------------------------------------------------------------
// Wire → view-model converter
//
// `LeadWire` is `components["schemas"]["LeadResponse"]` from codegen. The
// backend ships every field the view-model needs (including the denormalised
// `interestedPropertyBuildingName` and the `desired*` / `lostReason`
// pipeline overlays). The converter only normalises `undefined → null`
// everywhere the view-model promises `T | null`, plus keeps the embedded
// `organizationId` field at 0 since the API intentionally omits it (the
// client already knows which tenant it operates as).
// ---------------------------------------------------------------------------

export function toLead(wire: LeadWire): Lead {
  return {
    id: wire.id!,
    organizationId: 0,
    customerId: wire.customerId!,
    customer: toCustomer(wire.customer ?? ({} as CustomerWire)),
    assignedBrokerId: wire.assignedBrokerId ?? null,
    interestedPropertyId: wire.interestedPropertyId ?? null,
    interestedPropertyBuildingName: wire.interestedPropertyBuildingName ?? null,
    interestedPropertyInternalCode: wire.interestedPropertyInternalCode ?? null,
    desiredListingType: wire.desiredListingType ?? null,
    desiredWard: wire.desiredWard ?? null,
    budgetMin: wire.budgetMin ?? null,
    budgetMax: wire.budgetMax ?? null,
    status: wire.status!,
    lostReason: (wire.lostReason as LeadLostReason | undefined) ?? null,
    nextFollowUpAt: wire.nextFollowUpAt ?? null,
    notes: wire.notes ?? null,
    source: wire.source ?? null,
    createdAt: wire.createdAt!,
    updatedAt: wire.updatedAt!,
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export interface LeadListQuery {
  assignedBrokerId?: number;
  /** Filter to one customer's leads (the customer detail's needs/properties). */
  customerId?: number;
}

export const leadKeys = {
  all: ["leads"] as const,
  lists: () => [...leadKeys.all, "list"] as const,
  list: (query: LeadListQuery) => [...leadKeys.lists(), query] as const,
  details: () => [...leadKeys.all, "detail"] as const,
  detail: (id: number) => [...leadKeys.details(), id] as const,
};

// ---------------------------------------------------------------------------
// Network calls
// ---------------------------------------------------------------------------

function buildLeadListPath(query: LeadListQuery): string {
  const params = new URLSearchParams();
  if (query.assignedBrokerId !== undefined) {
    params.set("assignedBrokerId", String(query.assignedBrokerId));
  }
  if (query.customerId !== undefined) {
    params.set("customerId", String(query.customerId));
  }
  const qs = params.toString();
  return qs ? `/leads?${qs}` : "/leads";
}

async function fetchLeads(query: LeadListQuery): Promise<Lead[]> {
  const wires = await apiRequest<LeadWire[]>(buildLeadListPath(query));
  return wires.map(toLead);
}

async function fetchLead(id: number): Promise<Lead> {
  return toLead(await apiRequest<LeadWire>(`/leads/${id}`));
}

async function postLead(body: LeadCreateRequest): Promise<Lead> {
  return toLead(
    await apiRequest<LeadWire>("/leads", { method: "POST", body }),
  );
}

async function patchLead(id: number, body: LeadPatchRequest): Promise<Lead> {
  // The backend exposes a dedicated /leads/:id/status endpoint for status
  // transitions (so authz/audit can attach to it cleanly). When the patch
  // is status-only, route there; otherwise use the generic PATCH.
  const keys = Object.keys(body);
  if (keys.length === 1 && keys[0] === "status" && body.status !== undefined) {
    return toLead(
      await apiRequest<LeadWire>(`/leads/${id}/status`, {
        method: "PATCH",
        body: { status: body.status },
      }),
    );
  }
  return toLead(
    await apiRequest<LeadWire>(`/leads/${id}`, { method: "PATCH", body }),
  );
}

function deleteLeadApi(id: number) {
  return apiRequest<void>(`/leads/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLeads(query: LeadListQuery = {}) {
  return useQuery({
    queryKey: leadKeys.list(query),
    queryFn: () => fetchLeads(query),
  });
}

/**
 * Fetch a single lead by id. Mirrors `useProperty(id)` in the property
 * feature — `enabled` gates on the id so the hook is safe to call with
 * `undefined` while routing parses the URL param.
 */
export function useLead(id: number | undefined) {
  return useQuery({
    queryKey: id !== undefined ? leadKeys.detail(id) : leadKeys.details(),
    queryFn: () => {
      if (id === undefined) throw new Error("useLead called without an id");
      return fetchLead(id);
    },
    enabled: id !== undefined,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: LeadCreateRequest) => postLead(body),
    onSuccess: (lead) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
    },
  });
}

// ---------------------------------------------------------------------------
// useCreateLeadFlow — orchestrates the two-step create against the real
// backend's `customerId`-only contract.
//
// 1. If `existingCustomerId !== null` → POST /leads directly with that id.
// 2. Otherwise → POST /customers first, then POST /leads with the returned
//    customer id.
//
// Error model:
//   - 409 CUSTOMER_PHONE_DUPLICATE on the customer step is a race condition
//     (another agent created the same phone after our lookup). The caller
//     catches the thrown ApiError and shows an inline phone-field error
//     with a reload CTA.
//   - If customer creation succeeds but lead creation fails, the orphan
//     customer is intentionally kept (no rollback): the user typed in
//     real customer data; they can still find the row via /customers.
//     The caller shows a toast on the lead error.
//   - Validation errors (4xx) from POST /customers propagate as ApiError
//     so the caller can map them onto the form via
//     `applyServerValidationErrors`.
// ---------------------------------------------------------------------------

export interface CreateLeadFlowInput {
  existingCustomerId: number | null;
  newCustomer: CustomerCreateRequest | null;
  leadFields: Omit<LeadCreateRequest, "customerId">;
}

export function useCreateLeadFlow() {
  const queryClient = useQueryClient();
  const createCustomer = useCreateCustomer();
  return useMutation({
    mutationFn: async (input: CreateLeadFlowInput): Promise<Lead> => {
      let customerId: number;
      if (input.existingCustomerId !== null) {
        customerId = input.existingCustomerId;
      } else if (input.newCustomer !== null) {
        const created = await createCustomer.mutateAsync(input.newCustomer);
        customerId = created.id;
      } else {
        throw new Error("No customer information provided");
      }
      return postLead({ customerId, ...input.leadFields });
    },
    onSuccess: (lead) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
    },
  });
}

interface PatchLeadInput {
  id: number;
  patch: LeadPatchRequest;
}

export function usePatchLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: PatchLeadInput) => patchLead(id, patch),
    // Optimistically update every cached list AND the lead's detail entry
    // so both the kanban and the /leads/:id page repaint before the server
    // replies. The detail page renders from leadKeys.detail(id); without
    // the detail write a status change there stayed invisible until a full
    // reload. We snapshot the previous data and roll back on error.
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: leadKeys.lists() });
      await queryClient.cancelQueries({ queryKey: leadKeys.detail(id) });
      const previousLists = queryClient.getQueriesData<Lead[]>({
        queryKey: leadKeys.lists(),
      });
      for (const [key, data] of previousLists) {
        if (!data) continue;
        queryClient.setQueryData<Lead[]>(
          key,
          data.map((lead) => (lead.id === id ? { ...lead, ...patch } : lead)),
        );
      }
      const previousDetail = queryClient.getQueryData<Lead>(
        leadKeys.detail(id),
      );
      if (previousDetail) {
        queryClient.setQueryData<Lead>(leadKeys.detail(id), {
          ...previousDetail,
          ...patch,
        });
      }
      return { previousLists, previousDetail };
    },
    onError: (_err, { id }, context) => {
      if (!context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
      if (context.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(id), context.previousDetail);
      }
    },
    onSuccess: (lead) => {
      // Server truth for the detail entry — the PATCH returns the updated
      // lead, so don't wait for the refetch round-trip.
      queryClient.setQueryData(leadKeys.detail(lead.id), lead);
    },
    onSettled: (_lead, _err, { id }) => {
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: leadKeys.detail(id) });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteLeadApi(id),
    onSuccess: (_data, id) => {
      // Remove from every cached list.
      const lists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      for (const [key, data] of lists) {
        if (!data) continue;
        queryClient.setQueryData<Lead[]>(
          key,
          data.filter((lead) => lead.id !== id),
        );
      }
      queryClient.removeQueries({ queryKey: leadKeys.detail(id) });
    },
  });
}
